/**
 * Titre par défaut d'une note créée depuis le DocReader.
 *
 * Une note source s'intitulait par les 60 premiers caractères de la citation :
 * rien n'indiquait de quel document ni de quel endroit elle venait. Le titre
 * annonce désormais le document lu — son titre humanisé, « Saint-Martin vie »
 * et non « Saint-Martin_vie.pdf » (30/09/2026) — et la position de la sélection.
 *
 * `formatLocatorLabel` est la source unique de l'écriture d'un repère : la
 * barre de sélection l'affiche en aperçu et le titre de la note le reprend
 * — les deux ne peuvent plus diverger.
 */

import type { LocatorExtent } from './locatorUtils';
import { documentTitleFromFileName } from '../../../utils/fileNameLabel';

/** Repères de position d'une sélection, tels que la toolbar et le sourceRef les portent. */
export interface LocatorLabelInput {
  page?: number;
  /** Dernière page d'une sélection qui en chevauche plusieurs (PDF) ; les lignes sont relatives à leur page */
  pageEnd?: number;
  lineStart?: number;
  lineEnd?: number;
  paragraphLineStart?: number;
  paragraphLineEnd?: number;
  paragraphEstimate?: number;
  cfi?: string;
  /** EPUB : numéro de chapitre (1-based, index de spine) et libellé de la table des matières */
  chapter?: number;
  chapterLabel?: string;
}

/** Longueur max du titre du document dans le titre de la note : au-delà, les repères deviennent illisibles. */
const MAX_FILE_NAME_LENGTH = 80;

/** Longueur max du libellé de chapitre dans un repère (le titre de la note le reprend). */
const MAX_CHAPTER_LABEL_LENGTH = 50;

/** Longueur de l'extrait de texte servant de repli quand le nom de fichier manque. */
const FALLBACK_TEXT_LENGTH = 60;

/**
 * Résumé lisible de la position d'une sélection : `p.12, l.3-8`, `p.214`,
 * `p.12, l.40 – p.13, l.3` (sélection chevauchant deux pages),
 * `chap. 3 « III. La tempête »` (EPUB), ou en dernier repli le CFI brut
 * `CFI: /6/14[chap03]!/4/2/2`. Chaîne vide si aucun repère n'est disponible.
 *
 * La position estimée (`paragraphEstimate`, 1–99) n'est jamais affichée
 * (30/09/2026) : elle reste enregistrée pour « Voir dans le document », mais
 * « ~pos.20 » ne dit rien au lecteur — un passage HTML/DOCX n'a donc aucun
 * repère affiché. Miroir : `formatPassageLabel` (api/src/utils/passageSource.ts).
 *
 * `extent` doit décrire l'étendue réelle du CONTENU de la note, comme pour
 * `buildSelectionLocators` : un titre annonçant `l.7` sur une note dont les
 * locators couvrent `l.6-9` mentirait sur ce qu'elle contient.
 */
export function formatLocatorLabel(
  input: LocatorLabelInput,
  extent: LocatorExtent = 'selection',
): string {
  const parts: string[] = [];

  const useParagraph = extent === 'paragraph' && Boolean(input.paragraphLineStart);
  const lineStart = useParagraph ? input.paragraphLineStart : input.lineStart;
  const lineEnd = useParagraph
    ? (input.paragraphLineEnd ?? input.paragraphLineStart)
    : (input.lineEnd ?? input.lineStart);

  // Sélection chevauchant plusieurs pages : les numéros de ligne sont relatifs
  // à leur page, « l.40-3 » n'aurait aucun sens — chaque borne est écrite avec
  // sa page (`p.12, l.40 – p.13, l.3`). Sans lignes, la plage de pages suffit.
  const spansPages = Boolean(input.page && input.pageEnd && input.pageEnd > input.page);

  if (spansPages && lineStart) {
    parts.push(`p.${input.page}, l.${lineStart} – p.${input.pageEnd}, l.${lineEnd ?? lineStart}`);
  } else {
    if (input.page) parts.push(spansPages ? `p.${input.page}-${input.pageEnd}` : `p.${input.page}`);

    if (lineStart) {
      parts.push(lineEnd && lineEnd !== lineStart ? `l.${lineStart}-${lineEnd}` : `l.${lineStart}`);
    }
  }

  // EPUB : le chapitre est le repère humain ; le CFI, illisible, ne sert de
  // repli que lorsque le chapitre n'a pas pu être résolu.
  if (input.chapter) {
    const label = input.chapterLabel?.trim();
    const shortLabel = label && label.length > MAX_CHAPTER_LABEL_LENGTH
      ? `${label.substring(0, MAX_CHAPTER_LABEL_LENGTH)}…`
      : label;
    parts.push(shortLabel ? `chap. ${input.chapter} « ${shortLabel} »` : `chap. ${input.chapter}`);
  } else if (input.cfi) {
    parts.push(`CFI: ${input.cfi}`);
  }

  return parts.join(', ');
}

/**
 * Titre par défaut : `titre du document — repères` (« Saint-Martin vie —
 * p.12, l.3-8 », « Le Capital — chap. 3 »). Sans repère, le titre seul
 * (« Carnet de lecture ») ; sans nom de fichier — cas théorique,
 * `file.metadata?.name || file.name` est toujours renseigné —, l'extrait de
 * texte d'origine, pour ne jamais produire un titre vide. Le titre du document
 * est `documentTitleFromFileName` (extension retirée, `_` → espace), miroir de
 * `deriveSourceNoteTitle` côté serveur.
 */
export function buildSourceNoteTitle(
  fileName: string | undefined,
  input: LocatorLabelInput,
  extent: LocatorExtent,
  fallbackText: string,
): string {
  if (!fileName) {
    return fallbackText.length > FALLBACK_TEXT_LENGTH
      ? `${fallbackText.substring(0, FALLBACK_TEXT_LENGTH)}...`
      : fallbackText;
  }

  const documentTitle = documentTitleFromFileName(fileName);
  const name = documentTitle.length > MAX_FILE_NAME_LENGTH
    ? `${documentTitle.substring(0, MAX_FILE_NAME_LENGTH - 1)}…`
    : documentTitle;

  const label = formatLocatorLabel(input, extent);
  return label ? `${name} — ${label}` : name;
}
